import React from 'react';
import clsx from 'clsx';
import { TCITooltip } from '@/components/ui/Tooltip';
import useFlags from '@/hooks/useFlags';
import useLessonOverviewContext from '@/components/shared/NewLessonOverview/useLessonOverviewContext';
import tooltipIndicatorIcon from 'images/icons/triangle-indicator.svg';
import handsOnIcon from 'images/icons/hands-on.svg';
import CardTitle from './CardTitle';

interface CardBodyProps {
  ariaLabel: string;
  iconColor: string;
  iconSrc: string;
  linkHref: string;
  linkLabel: string;
  openInNewWindow?: boolean;
  page?: number;
  showThumbnail?: boolean;
  thumbnail?: string;
  time?: string;
  tooltipIndicator?: string;
  topLabel?: string;
  truncateTextTooltip?: boolean;
}

export const CardBody = ({
  ariaLabel,
  iconColor,
  iconSrc,
  linkHref,
  linkLabel,
  openInNewWindow = false,
  page,
  showThumbnail = false,
  thumbnail = '/placeholder.png',
  time,
  tooltipIndicator,
  topLabel,
  truncateTextTooltip = false,
}: CardBodyProps) => {
  const isStaffer = useLessonOverviewContext(state => state.isStaffer);
  const programColor = useLessonOverviewContext(state => state.programColor);
  const { accessibilityReflow } = useFlags();
  const target = openInNewWindow ? '_blank' : '';

  return (
    <div
      className={clsx(
        'tw-relative tw-flex tw-items-start tw-justify-start tw-flex-col tw-flex-grow',
        "after:tw-content-[''] after:tw-absolute after:tw-bottom-0.5 after:tw-right-2",
        'after:tw-h-px after:tw-bg-med-grey',
        {
          'after:tw-left-2 tw-p-2 tw-pb-6': showThumbnail,
          'after:tw-left-[96px]': !showThumbnail,
        },
      )}
    >
      {!!tooltipIndicator && isStaffer && (
        <TCITooltip>
          <TCITooltip.Trigger asChild>
            <img
              alt={tooltipIndicator}
              className="tw-absolute tw-right-0 tw-top-0 tw-m-1"
              src={tooltipIndicatorIcon}
            />
          </TCITooltip.Trigger>
          <TCITooltip.Content>
            {tooltipIndicator}
          </TCITooltip.Content>
        </TCITooltip>
      )}
      {showThumbnail && (
        <a aria-hidden="true" className="tw-w-full" href={linkHref} tabIndex={-1} target={target}>
          <img
            aria-hidden
            alt=""
            className={clsx('tw-h-[8rem] tw-w-full tw-object-cover tw-rounded-lg tw-mb-4', {
              'lg:tw-h-[10rem]': accessibilityReflow,
            })}
            src={thumbnail}
          />
        </a>
      )}
      <div className="tw-flex tw-w-full">
        <a
          aria-hidden
          aria-label={ariaLabel}
          className={clsx(
            'tw-border-none tw-rounded-tl-lg tw-text-white tw-flex tw-flex-shrink-0 tw-items-center tw-justify-center tw-cursor-pointer', {
              'tw-w-12 tw-h-12 tw-absolute tw-top-2 tw-left-2': showThumbnail,
              'tw-w-24 tw-h-24': !showThumbnail,
            },
          )}
          href={linkHref}
          style={{ backgroundColor: iconColor }}
          tabIndex={-1}
          target={target}
        >
          <img
            alt={ariaLabel}
            className={showThumbnail ? 'tw-w-7 tw-h-7' : 'tw-w-12 tw-h-12'}
            src={iconSrc || handsOnIcon}
            style={{ filter: 'brightness(100)' }}
          />
        </a>
        <div className="tw-flex-grow tw-min-h-[3rem]">
          <CardTitle
            halfCard={!showThumbnail}
            linkHref={linkHref}
            linkLabel={linkLabel}
            openInNewWindow={openInNewWindow}
            page={page}
            programColor={programColor}
            time={time}
            topLabel={topLabel}
            truncateTextTooltip={truncateTextTooltip}
          />
        </div>
      </div>
    </div>
  );
};
